import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { Company } from '../../pages/requests/interfaces/requests.interface';
import {
  CrmCustomerAgencyInvitation,
  ChangeUserRole,
  Child,
  ChildReq,
  CrmCustomerCreatePrecheckResponse,
  CreateManager,
  Customer,
  CustomerPushStats,
  CustomerReq,
  PersonalData,
  PersonalDataReq,
  User,
  UserProfile,
  UserDetailsInformation,
  ValidationFioReq,
} from '../interfaces/users.interface';
import { _DeepPartialObject, DeepPartial } from 'chart.js/dist/types/utils';
import { CustomerCreatorEnum, CustomerTypeEnum } from '../../../shared/enums/customer.enum';
import { FIO } from '../../../shared/services/tools.service';
import { Avatar, IFile } from '../../../shared/interfaces/files.interface';

@Injectable()
export class UsersService {
  constructor(private http: HttpClient) {}

  getUser(id: string): Observable<User> {
    return this.http.get<User>(`${environment.apiUrl}/users/crm/get-user-by-id/${id}`);
  }

  getMe(): Observable<User> {
    return this.http.get<User>(`${environment.apiUrl}/users/get-me`);
  }

  getAllCustomers(): Observable<Customer[]> {
    return this.http.get<Customer[]>(`${environment.apiUrl}/users/crm/get-all-customers`);
  }

  getCustomerPushStats(): Observable<CustomerPushStats> {
    return this.http.post<CustomerPushStats>(
      `${environment.apiUrl}/users/crm/customers-push-stats`,
      {}
    );
  }

  getCustomersInvitedBy(userId: string): Observable<Customer[]> {
    return this.http.get<Customer[]>(
      `${environment.apiUrl}/users/crm/get-customers-invited-by/${userId}`
    );
  }

  createCustomer(
    phone: string,
    personalData?: PersonalDataReq,
    userDetailsInformation?: _DeepPartialObject<UserDetailsInformation>,
    options: {
      createdByType?: CustomerCreatorEnum;
      createdByAgentId?: string;
      invitedBy?: string;
    } = {}
  ): Observable<Customer> {
    return this.http.post<Customer>(`${environment.apiUrl}/users/crm/create-customer`, {
      phone,
      personalData,
      userDetailsInformation,
      ...options,
    });
  }

  checkCustomerBeforeCreate(phone: string): Observable<CrmCustomerCreatePrecheckResponse> {
    return this.http.post<CrmCustomerCreatePrecheckResponse>(
      `${environment.apiUrl}/users/crm/check-customer-before-create`,
      { phone }
    );
  }

  createCustomerAgencyInvitation(payload: {
    customerUserId?: string;
    phone?: string;
    source?: 'crm_create_customer' | 'crm_create_request' | 'manual';
    meta?: Record<string, any>;
  }): Observable<CrmCustomerAgencyInvitation> {
    return this.http.post<CrmCustomerAgencyInvitation>(
      `${environment.apiUrl}/tenants/invitations`,
      payload
    );
  }

  updateCustomer(customerId: string, data: DeepPartial<CustomerReq>) {
    return this.http.post<Customer>(
      `${environment.apiUrl}/users/update-customer/${customerId}`,
      data
    );
  }

  updateUser(userId: string, data: DeepPartial<User>): Observable<User> {
    const _data = { ...data };
    delete _data.role; //чтобы гарантировать, что на бэк не идет изменение роли

    return this.http.post<User>(`${environment.apiUrl}/users/update-user/${userId}`, _data);
  }

  updateUserProfile(userId: string, data: UserProfile): Observable<User> {
    return this.http.post<User>(`${environment.apiUrl}/users/update-profile/${userId}`, data);
  }

  updateUserDetails(userId: string, data: Partial<UserDetailsInformation>): Observable<User> {
    return this.http.post<User>(`${environment.apiUrl}/users/update-user-details/${userId}`, data);
  }

  getCustomerByUserId(userId: string) {
    return this.http.get<Customer>(
      `${environment.apiUrl}/users/crm/get-full-customer-by-user-id/${userId}`
    );
  }

  getChatMemberCustomerByUserId(chatId: string, userId: string) {
    return this.http.get<Customer>(
      `${environment.apiUrl}/chat/crm/chats/${chatId}/members/${userId}/customer-profile`
    );
  }

  removeUser(userId: string): Observable<string> {
    return this.http.delete<string>(`${environment.apiUrl}/users/delete-user/${userId}`);
  }

  setUserBlockStatus(userId: string, blocked: boolean): Observable<User> {
    return this.http.patch<User>(`${environment.apiUrl}/users/crm/block-user/${userId}`, {
      blocked,
    });
  }

  changeUserPhone(userId: string, phone: string): Observable<User> {
    return this.http.patch<User>(`${environment.apiUrl}/users/crm/change-phone/${userId}`, {
      phone,
    });
  }

  addCompanyToUser(customerId: string, data: Company): Observable<Customer> {
    return this.http.post<Customer>(
      `${environment.apiUrl}/users/add-company-to-user/${customerId}`,
      data
    );
  }

  createChild(data: ChildReq): Observable<Child> {
    return this.http.post<Child>(`${environment.apiUrl}/users/create-child`, data);
  }

  getChild(childId: string): Observable<Child> {
    return this.http.get<Child>(`${environment.apiUrl}/users/get-child/${childId}`);
  }

  getChildren(): Observable<Child[]> {
    return this.http.get<Child[]>(`${environment.apiUrl}/users/get-children`);
  }

  updateChild(childId: string, data: DeepPartial<ChildReq>): Observable<Child> {
    return this.http.post<Child>(`${environment.apiUrl}/users/update-child/${childId}`, data);
  }

  removeChild(childId: string): Observable<string> {
    return this.http.delete<string>(`${environment.apiUrl}/users/delete-child/${childId}`);
  }

  // addPersonToUser(
  //   customerId: string,
  //   data: AddPersonReq
  // ): Observable<Customer> {
  //   return this.http.post<Customer>(
  //     `${environment.apiUrl}/users/add-person-to-user/${customerId}`,
  //     data
  //   );
  // }

  getParticipant(userId: string, participantId?: string): Observable<PersonalData> {
    return this.http.post<PersonalData>(`${environment.apiUrl}/users/get-participant-by-options`, {
      userId,
      participantId: participantId ?? null,
    });
  }

  createManager(data: CreateManager): Observable<User> {
    return this.http.post<User>(`${environment.apiUrl}/users/create-manager/`, data);
  }

  createAgent(data: CreateManager): Observable<User> {
    return this.http.post<User>(`${environment.apiUrl}/users/create-agent/`, data);
  }

  getAllManagers(): Observable<User[]> {
    return this.http.get<User[]>(`${environment.apiUrl}/users/crm/get-all-managers`);
  }

  getAllManagersPersonalData() {
    return this.http.get<Customer[]>(
      `${environment.apiUrl}/users/crm/get-all-managers-personal-data`
    );
  }

  uploadPassportScan(file: File, customerId: string) {
    const formData: FormData = new FormData();
    formData.append('file', file, file.name);

    return this.http.post<IFile>(
      `${environment.apiUrl}/users/upload-passport-scan/${customerId}`,
      formData
    );
  }

  uploadChildPassportScan(file: File, childId: string) {
    const formData: FormData = new FormData();
    formData.append('file', file, file.name);

    return this.http.post<IFile>(
      `${environment.apiUrl}/users/upload-child-passport-scan/${childId}`,
      formData
    );
  }

  getCustomerTypeText(type?: CustomerTypeEnum) {
    switch (type) {
      case CustomerTypeEnum.Default:
        return 'Не выбран';
      case CustomerTypeEnum.Bodium:
        return 'Bodium';
      default:
        return 'Не выбран';
    }
  }

  setPromoCode(promoCode: string, customerId: string): Observable<Customer> {
    return this.http.post<Customer>(`${environment.apiUrl}/users/set-promocode`, {
      promoCode,
      customerId,
    });
  }

  validateCustomerFio(data: ValidationFioReq) {
    return this.http.post<boolean>(`${environment.apiUrl}/users/validate-fio`, data);
  }

  //возращает юзер, которого апдейтили
  changeUserRole(data: ChangeUserRole) {
    return this.http.patch<User>(`${environment.apiUrl}/users/crm/update-role/${data.userId}`, {
      role: data.newRole,
    });
  }
}
